/**
 * GenerateApiKeyModal Component
 *
 * Form modal for generating a new API key, optionally with roles.
 * The actual creation is delegated via the `onSubmit` callback.
 *
 * Uses `useTranslation('modules.modals.generateApiKey')` for labels.
 *
 * @example
 * ```tsx
 * <GenerateApiKeyModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSubmit={async (data) => {
 *     const result = await createApiKey(data);
 *     return result;
 *   }}
 * />
 * ```
 */
import * as React from 'react';
import { useState } from 'react';
import { type TestIdProps } from '../../utils';
import { FormModal } from '../../components/FormModal';
import { Input } from '../../components/inputs';
import { InputWrapper } from '../../components/InputWrapper';
import { Select } from '../../components/selects/Select';
import { Textarea } from '../../components/Textarea';
import { useTranslation } from '../../providers/UIProvider';

export interface GenerateApiKeyFormData {
  name: string;
  description: string;
  rateLimitPerMin: number;
  /** Selected roles; empty when none is picked or no `roleOptions` are given */
  roles: string[];
}

export interface GenerateApiKeyModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the modal is dismissed */
  onClose: () => void;
  /** Roles the key may be given, each optionally described; the field shows only when given */
  roleOptions?: Array<{ value: string; label: string; description?: string }>;
  /** Called with form data — should return on success or throw on error */
  onSubmit: (data: GenerateApiKeyFormData) => Promise<void>;
  /** Whether the submission is in progress (externally controlled) */
  loading?: boolean;
  /** External error message */
  error?: string | null;
}

export const GenerateApiKeyModal: React.FC<GenerateApiKeyModalProps> = ({
  open,
  onClose,
  roleOptions,
  onSubmit,
  loading = false,
  error: externalError
}) => {
  const t = useTranslation('modules.modals.generateApiKey');
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    rateLimitPerMin: string;
    roles: string[];
  }>({
    name: '',
    description: '',
    rateLimitPerMin: '1000',
    roles: []
  });
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLocalError(null);

    if (!formData.name.trim()) {
      setLocalError(t('errors.nameRequired'));
      return;
    }

    const rateLimit = parseInt(formData.rateLimitPerMin, 10);
    if (isNaN(rateLimit) || rateLimit <= 0) {
      setLocalError(t('errors.rateLimitInvalid'));
      return;
    }

    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        rateLimitPerMin: rateLimit,
        roles: formData.roles
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('errors.createError');
      setLocalError(errorMsg);
    }
  };

  const displayError = localError || externalError || null;

  return (
    <FormModal
      open={open}
      title={t('title')}
      submitLabel={loading ? t('status.generating') : t('title')}
      cancelLabel={t('actions.cancel')}
      loading={loading}
      submitDisabled={!formData.name.trim()}
      error={displayError}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <InputWrapper label={t('fields.name')} htmlFor='name' labelSize='sm' required>
        <Input
          id='name'
          type='text'
          placeholder={t('placeholders.name')}
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          disabled={loading}
          required
        />
      </InputWrapper>

      <InputWrapper
        label={t('fields.description')}
        htmlFor='description'
        labelSize='sm'
        required={false}
      >
        <Textarea
          id='description'
          placeholder={t('placeholders.description')}
          value={formData.description}
          onInput={e =>
            setFormData({ ...formData, description: (e.target as HTMLTextAreaElement).value })
          }
          disabled={loading}
          size='md'
          resizable='y'
        />
      </InputWrapper>

      <InputWrapper
        label={t('fields.rateLimit')}
        htmlFor='rateLimitPerMin'
        labelSize='sm'
        required
        hint={t('placeholders.rateLimitHint')}
      >
        <Input
          id='rateLimitPerMin'
          type='number'
          placeholder='1000'
          value={formData.rateLimitPerMin}
          onChange={e => setFormData({ ...formData, rateLimitPerMin: e.target.value })}
          disabled={loading}
          required
          rightContent={<span className='text-xs text-foreground-muted'>/min</span>}
        />
      </InputWrapper>

      {roleOptions && (
        <InputWrapper label={t('fields.roles')} htmlFor='roles' labelSize='sm' className='min-h-96'>
          <Select
            id='roles'
            testId='generate-api-key-roles'
            isMulti
            value={formData.roles}
            onChange={value =>
              setFormData({
                ...formData,
                roles: (Array.isArray(value) ? value : [value]).map(String)
              })
            }
            options={roleOptions}
            disabled={loading}
          />
        </InputWrapper>
      )}
    </FormModal>
  );
};

GenerateApiKeyModal.displayName = 'GenerateApiKeyModal';
